import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './ApprovedComponentList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialApprovedComponentState } from '../../reducers/initialState';
import ApprovedComponentListTable from './ApprovedComponentListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Cookies from 'js-cookie';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ModelCategoryConfigurationContentTemplate from '../ModelCategoryTab/ModelCategoryConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import { getModelCategoryPermission } from '../../utils';


const breadcrumbItems = [
  { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
  { label: <FormattedMessage id="approvedComponents.approvedComponents" defaultMessage="Approved components" /> } // Last item has no href (current page)
];


const ApprovedComponentList = ({ approvedComponent = { ...initialApprovedComponentState.approvedComponent }, userProfileWidgets, intl, getApprovedComponent, userModelCategories,
  saveApprovedComponent, setCurrentPage, setFilterData, resetModelComponent }) => {
  const history = useNavigate();

  let filter = '';
  let offset = 0;
  let limit = 1000;
  let { ModelCategoryId } = useParams();

  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  useEffect(() => {
    getApprovedComponent(ModelCategoryId, offset, limit, filter);
  }, [getApprovedComponent, offset, limit, filter, ModelCategoryId, approvedComponent.isOpSuccessful]);

  useEffect(() => {
    return () => {
      resetModelComponent({ ...initialApprovedComponentState });
    }
  }, [])

  const [active, setActive] = useState(false);
  const [approvedComponentId, setApprovedComponentId] = useState(0);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };


  const searchHandler = value => {
    setCurrentPage(1);
    filteredResults(value);
  };

  const filteredResults = value => {
    let filteredList = approvedComponent.approvedComponentList;

    if (value !== '') {
      filteredList = approvedComponent.approvedComponentList.filter(function (item) {
        return (
          item.ModelComponentType.toLowerCase().includes(value.toLowerCase()) ||
          item.Model.toLowerCase().includes(value.toLowerCase()) ||
          item.Brand.toLowerCase().includes(value.toLowerCase())
        );
      });
    }

    setFilterData({ filteredResults: { filteredResults: filteredList } });
  };


  const approvedComponentListObj = Object.values(approvedComponent.filteredResults);

  const onRemoveApprovedComponentClick = approvedComponentId => {
    setActive(true);
    setApprovedComponentId(approvedComponentId);
  };

  const DeleteApprovedComponent = () => {
    let saveData = approvedComponent.selectedApprovedComponent;
    saveData.PerformDelete = true;
    saveData.ModelCategoryId = ModelCategoryId;
    saveData.ApprovedComponentId = approvedComponentId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveApprovedComponent(saveData, 'APPROVEDCOMPONENT_DELETE');
  };

  const handleClose = () => {
    setActive(false);
    setApprovedComponentId(0);
  };

  const handleConfirm = () => {
    DeleteApprovedComponent();
    handleClose();
  };

  const onAddNewClick = () => {
    history(`/operational-setup/model-category-configuration/${ModelCategoryId}/approved-component/0`);
  };

  const onViewApprovedComponentClick = approvedComponentId => {
    history(`/operational-setup/model-category-configuration/${ModelCategoryId}/approved-component-view/${approvedComponentId}`);
  };

  let messageId = (approvedComponent && approvedComponent.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });


  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'APPROVEDCOMPONENT_EDIT');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'APPROVEDCOMPONENT_DELETE');
  const isViewAllowed = IsWidgetAccessible(userProfileWidgets, 'APPROVEDCOMPONENT_VIEW');

  const dialogTitle = intl.formatMessage({
    id: 'approvedComponents.confirmDelete',
    defaultMessage: 'Are you sure you want to delete approved component?'
  });

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)


  return (
    <div className={s.approvedComponent}>
      {approvedComponent.isLoading && <LoadingSpinner />}
      {approvedComponent.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={approvedComponent.showBanner}
          status={approvedComponent.isOpSuccessful}
          successText={messageText}
        />
      )}
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />

      <ModelCategoryConfigurationContentTemplate selectedPage="manageApprovedComponents" userProfileWidgets={userProfileWidgets}>
        <div id="approved-component-section">
          <div className={s.approvedComponentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="approvedComponents.approvedComponents" defaultMessage="Approved components" />
              <span className={s.circle}>
                {approvedComponent.approvedComponentList.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.titleRows}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                disabled={approvedComponent.showAddEditScreen}
                // initialText={filter}
                placeHolderTextId="approvedComponent.search"
                data-unittest="searchApprovedComponents"
              />
            </Col>
            <Col sm={6} lg={8}>

              {isModelCategoryOwner && <Button
                className={s.addbutton}
                style={WidgetVisibility(userProfileWidgets, 'APPROVEDCOMPONENT_ADDNEW')}
                onClick={onAddNewClick}
                disabled={approvedComponent.showAddEditScreen}
                data-unittest="addApprovedComponentButton"
              >
                + <FormattedMessage id="approvedComponent.addNew" defaultMessage="ADD NEW" />
              </Button>}
            </Col>
          </Row>
          <div>
            <Pagination
              data={approvedComponentListObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={approvedComponent.currentPage}
            >
              <ApprovedComponentListTable
                RemoveApprovedComponent={onRemoveApprovedComponentClick}
                ViewApprovedComponent={onViewApprovedComponentClick}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                ViewAllowed={isViewAllowed}
                IsModelCategoryOwner={isModelCategoryOwner}

              />
            </Pagination>
          </div>

        </div>
      </ModelCategoryConfigurationContentTemplate>

    </div>
  );
};


ApprovedComponentList.propTypes = {
  approvedComponent: PropTypes.object.isRequired,
  userProfileWidgets: PropTypes.object.isRequired,
  getApprovedComponent: PropTypes.func.isRequired,
  saveApprovedComponent: PropTypes.func.isRequired,
  setApprovedComponentChange: PropTypes.func.isRequired
};

export default injectIntl(ApprovedComponentList);
